import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import PartnerSsoSettings from './PartnerSsoSettings';
import PartnerUsers from './PartnerUsers';
import partnerService from '../services/partnerService';
import revShareService from '../services/revShareService';
import CredentialsTab from './CredentialsTab';
import PartnerSyncTemplates from './PartnerSyncTemplates';
import { BillingTemplatesList } from './BillingTemplates';
import { SubTabs } from './shared/Tabs';
import './shared/Tabs.css';
import PartnerHotelChains from './PartnerHotelChains';
import PartnerDetailsTab from './PartnerDetailsTab';
import BillingTab from './BillingTab';
import OhipTransactionsTab from './OhipTransactionsTab';
import PartnerSupportTickets from './PartnerSupportTickets';
import VendorsPage from './VendorsPage';
import './Settings.css';
import './PartnerSettings.css';
import './PartnerForm.css';
import StripeConnectPanel from './StripeConnectPanel';
import ProductNotificationsTab from './shared/ProductNotificationsTab';
import { Bell } from 'lucide-react';

const PartnerSettings = () => {
  const { isPartner, isSuperAdmin, solePartnerId: authSolePartnerId } = useAuth();
  const { selectedPartner } = useAccount();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // THE TAB IS IN THE URL, so a link to a tab opens on it, Back returns to the tab it left, and a
  // refresh does not land you on Hotel Chains. It was read from the query string on arrival but never
  // written on a click, so ?tab=vendors worked for anyone sent the link and for nobody who found the
  // screen themselves — and there was no way to send that link.
  const TABS = ['details', 'billing', 'chains', 'users', 'sso', 'stripe', 'revshare', 'credentials', 'vendors',
    'templates', 'transactions', 'notifications', 'support'];
  const [activeTab, setActiveTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return TABS.includes(t) ? t : 'chains';
  });

  // REPLACE, NOT PUSH. Ten tabs clicked while reading a partner would otherwise be ten entries to
  // reverse before Back leaves the screen. Stripe's return parameters are dropped as the tab is
  // recorded — they have done their job by then, and leaving them means a refresh re-triggers the
  // return handling.
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };


  // Rev share tab
  const [transfers, setTransfers] = useState(null);
  const [transfersLoading, setTransfersLoading] = useState(false);
  const [transfersError, setTransfersError] = useState(null);
  const [revShareFrom, setRevShareFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  });
  const [revShareTo, setRevShareTo] = useState(() => new Date().toISOString().slice(0, 10));

  // Follow the URL when it changes underneath us — a pasted link, Back, or Stripe returning. Every
  // known tab, rather than the three somebody remembered to add: a new tab used to need a line here as
  // well as a button, and the one that was forgotten simply could not be linked to.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('stripe_return') || params.get('stripe_refresh')) {
      setActiveTab('stripe');
      return;
    }
    const t = params.get('tab');
    if (t && TABS.includes(t)) setActiveTab(t);
  }, [location.search]); // eslint-disable-line

  // Always follow the partner selected in the top nav bar; fall back to the user's own
  // partner only when nothing is selected. (Previously partner users used authPartnerId,
  // which could resolve to a stale/deleted partner from an old accepted invitation.)
  const partnerId = selectedPartner || authSolePartnerId;

  useEffect(() => {
    if (partnerId && activeTab === 'revshare') {
      fetchTransfers();
    }
  }, [partnerId, activeTab]); // eslint-disable-line

  const fetchTransfers = async () => {
    setTransfersLoading(true);
    setTransfersError(null);
    try {
      const res = await revShareService.getTransfers(partnerId, revShareFrom, revShareTo);
      setTransfers(res.data);
    } catch (err) {
      setTransfersError('Failed to load revenue share data');
    } finally {
      setTransfersLoading(false);
    }
  };

  if (!isPartner && !isSuperAdmin) {
    return (
      <div className="settings-container">
        <div className="no-tenant-message" style={{ margin: '4rem auto', textAlign: 'center' }}>
          <h3>Access Denied</h3>
          <p>This page is only available to partner accounts.</p>
        </div>
      </div>
    );
  }

  if (!partnerId) {
    return (
      <div className="settings-container">
        <div className="no-tenant-message" style={{ margin: '4rem auto', textAlign: 'center' }}>
          <h3>No Partner Selected</h3>
          <p>Use the partner picker in the top bar to select a partner.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Partner Settings</h1>
        <p className="settings-subtitle">Manage users, SSO and Stripe settings for your partner account</p>
      </div>

      <div className="settings-tabs">
        <button
          className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => handleTabChange('details')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M10 6v4M10 13.5v.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
          </svg>
          <span className="tab-label">Details</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'billing' ? 'active' : ''}`}
          onClick={() => handleTabChange('billing')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M2 8h16" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M6 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span className="tab-label">Billing</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'chains' ? 'active' : ''}`}
          onClick={() => handleTabChange('chains')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M2 18V7l8-5 8 5v11" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 18v-5h4v5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 10h2M12 10h2M6 13h2M12 13h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span className="tab-label">Hotel Chains</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => handleTabChange('users')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
          </svg>
          <span className="tab-label">Users</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'sso' ? 'active' : ''}`}
          onClick={() => handleTabChange('sso')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="3" y="11" width="14" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M7 11V7a3 3 0 016 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="10" cy="14.5" r="1" fill="currentColor"/>
          </svg>
          <span className="tab-label">SSO</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'stripe' ? 'active' : ''}`}
          onClick={() => handleTabChange('stripe')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M2 8h16" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
          <span className="tab-label">Stripe</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'revshare' ? 'active' : ''}`}
          onClick={() => handleTabChange('revshare')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M10 6v1m0 6v1M7.5 9.5a2.5 2.5 0 015 0c0 1.38-2.5 2-2.5 2s-2.5.62-2.5 2a2.5 2.5 0 005 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span className="tab-label">Rev Share</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'credentials' ? 'active' : ''}`}
          onClick={() => handleTabChange('credentials')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="3" y="7" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M7 7V5a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="10" cy="12" r="1.5" fill="currentColor"/>
          </svg>
          <span className="tab-label">Credentials</span>
        </button>

        {/* VENDORS — the companies that call the Universal API on this partner's behalf. It sits here
            rather than under Admin because a vendor's credential IS held at partner level: one set of
            credentials working across every chain and hotel beneath this partner.
            Open to the partner's own admins: who integrates with a partner is the partner's business, and
            the endpoint resolves the partner from the caller rather than from the screen. */}
        <button
          className={`tab-button ${activeTab === 'vendors' ? 'active' : ''}`}
          onClick={() => handleTabChange('vendors')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M3 7h14v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M7 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M3 11h14" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
          <span className="tab-label">Vendors</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => handleTabChange('templates')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M7 7h6M7 10h6M7 13h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span className="tab-label">Templates</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => handleTabChange('transactions')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M3 5h14M3 10h14M3 15h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="15" cy="15" r="3" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M15 13.5v1.5l1 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
          </svg>
          <span className="tab-label">Transactions</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => handleTabChange('notifications')}
        >
          <Bell size={20} />
          <span className="tab-label">Notifications</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'support' ? 'active' : ''}`}
          onClick={() => handleTabChange('support')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M10 7a3 3 0 1 1 0 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="10" cy="15" r="0.75" fill="currentColor"/>
          </svg>
          <span className="tab-label">Support</span>
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'details' && (
          <div className="tab-content">
            <PartnerDetailsTab partnerId={partnerId} />
          </div>
        )}
        {/* Read-only for everyone but a superadmin — the lambda decides, the tab shows it. */}
        {activeTab === 'billing' && (
          <div className="tab-content">
            <BillingTab entity="partner" entityId={partnerId} />
          </div>
        )}
        {activeTab === 'chains' && (
          <div className="tab-content">
            <PartnerHotelChains />
          </div>
        )}
        {activeTab === 'users' && <PartnerUsers partnerId={partnerId} />}
        {activeTab === 'sso' && (
          <div className="tab-content">
            <PartnerSsoSettings partnerId={partnerId} />
          </div>
        )}
        {activeTab === 'stripe' && (
          <div className="tab-content">
            {/* No canWaive here. The waiver is a superadmin control on the partner's admin page
                (/partners/:id), not on the partner's own settings screen — this screen is what a partner
                sees about itself, and a partner is never the one who decides it can skip Stripe. */}
            <StripeConnectPanel
              partnerId={partnerId}
              returnPath="/partner-settings"
              reloadKey={activeTab}
            />
          </div>
        )}
        {activeTab === 'revshare' && (
          <div className="tab-content">
            <div style={{ marginBottom: '1.25rem' }}>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>Revenue Share Earnings</h3>
              <p style={{ margin: '0 0 0.875rem', fontSize: '0.875rem', color: '#64748b' }}>
                Transfers received from vault sales via Stripe Connect.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[
                  { label: 'Last 1 month', months: 1 },
                  { label: 'Last 3 months', months: 3 },
                  { label: 'Last 6 months', months: 6 },
                  { label: 'Last year', months: 12 },
                ].map(({ label, months }) => {
                  const from = new Date();
                  from.setMonth(from.getMonth() - months);
                  const fromStr = from.toISOString().slice(0, 10);
                  const toStr = new Date().toISOString().slice(0, 10);
                  const isActive = revShareFrom === fromStr && revShareTo === toStr;
                  return (
                    <button
                      key={months}
                      type="button"
                      onClick={() => { setRevShareFrom(fromStr); setRevShareTo(toStr); }}
                      style={{
                        padding: '4px 12px',
                        fontSize: '12px',
                        borderRadius: '5px',
                        border: isActive ? '1px solid #1e40af' : '1px solid #d1d5db',
                        background: isActive ? '#eff6ff' : '#f9fafb',
                        color: isActive ? '#1e40af' : '#374151',
                        cursor: 'pointer',
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '13px', color: '#374151', whiteSpace: 'nowrap' }}>From</label>
                <input
                  type="date"
                  value={revShareFrom}
                  onChange={e => setRevShareFrom(e.target.value)}
                  style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '13px' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '13px', color: '#374151', whiteSpace: 'nowrap' }}>To</label>
                <input
                  type="date"
                  value={revShareTo}
                  onChange={e => setRevShareTo(e.target.value)}
                  style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '13px' }}
                />
              </div>
              <button
                type="button"
                onClick={fetchTransfers}
                disabled={transfersLoading}
                style={{ padding: '5px 14px', background: '#1e40af', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '13px', cursor: 'pointer' }}
              >
                {transfersLoading ? 'Loading...' : 'Apply'}
              </button>
            </div>

            {transfersError && (
              <div style={{ padding: '10px 14px', background: '#fee2e2', color: '#dc2626', borderRadius: '6px', fontSize: '13px', marginBottom: '1rem' }}>
                {transfersError}
              </div>
            )}

            {transfersLoading && (
              <div style={{ color: '#64748b', fontSize: '13px' }}>Loading transfers...</div>
            )}

            {!transfersLoading && transfers && (
              <>
                {/* Summary card */}
                {transfers.partner && !transfers.partner.stripe_account_id ? (
                  <div style={{ padding: '1rem', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', fontSize: '13px', color: '#92400e' }}>
                    Stripe Connect not set up for this partner — no transfer data available.
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1', minWidth: '180px', padding: '16px 20px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Earned</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#15803d' }}>
                          {transfers.currency
                            ? new Intl.NumberFormat('en-US', { style: 'currency', currency: transfers.currency.toUpperCase() }).format(transfers.totalAmount / 100)
                            : '—'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>{transfers.transfers.length} transfer{transfers.transfers.length !== 1 ? 's' : ''}</div>
                      </div>
                    </div>

                    {/* Monthly breakdown */}
                    {transfers.transfers.length > 0 && (() => {
                      const byMonth = {};
                      transfers.transfers.forEach(t => {
                        const d = new Date(t.created * 1000);
                        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        if (!byMonth[key]) byMonth[key] = { amount: 0, count: 0, currency: t.currency };
                        byMonth[key].amount += t.amount;
                        byMonth[key].count++;
                      });
                      const months = Object.keys(byMonth).sort().reverse();
                      return (
                        <div style={{ marginBottom: '1.5rem' }}>
                          <h4 style={{ fontSize: '13px', fontWeight: 600, color: '#374151', margin: '0 0 10px' }}>Monthly Breakdown</h4>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                              <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Month</th>
                                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Transfers</th>
                                <th style={{ padding: '8px 10px', fontWeight: 600 }}>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {months.map(m => (
                                <tr key={m} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                  <td style={{ padding: '7px 10px' }}>{m}</td>
                                  <td style={{ padding: '7px 10px', color: '#6b7280' }}>{byMonth[m].count}</td>
                                  <td style={{ padding: '7px 10px', fontWeight: 600, color: '#15803d' }}>
                                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: byMonth[m].currency.toUpperCase() }).format(byMonth[m].amount / 100)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}

                    {/* Transfer list */}
                    <div>
                      <h4 style={{ fontSize: '13px', fontWeight: 600, color: '#374151', margin: '0 0 10px' }}>All Transfers</h4>
                      {transfers.transfers.length === 0 ? (
                        <p style={{ fontSize: '13px', color: '#9ca3af' }}>No transfers in this period.</p>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Date</th>
                              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Amount</th>
                              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Description</th>
                              <th style={{ padding: '8px 10px', fontWeight: 600, fontFamily: 'monospace', fontSize: '11px' }}>Transfer ID</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transfers.transfers.map(t => (
                              <tr key={t.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                                  {new Date(t.created * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </td>
                                <td style={{ padding: '7px 10px', fontWeight: 600, color: '#15803d' }}>
                                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: t.currency.toUpperCase() }).format(t.amount / 100)}
                                </td>
                                <td style={{ padding: '7px 10px', color: '#374151' }}>
                                  {t.description || (t.metadata && Object.keys(t.metadata).length > 0
                                    ? Object.entries(t.metadata).map(([k, v]) => `${k}: ${v}`).join(', ')
                                    : '—')}
                                </td>
                                <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontSize: '11px', color: '#9ca3af' }}>{t.id}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
        {activeTab === 'credentials' && (
          <div className="tab-content">
            <CredentialsTab entityType="partner" entityId={partnerId} />
          </div>
        )}
        {activeTab === 'templates' && (
          <div className="tab-content">
            <TemplatesTab partnerId={partnerId} />
          </div>
        )}
        {activeTab === 'vendors' && (
          <div className="tab-content">
            <VendorsPage partnerId={partnerId} embedded />
          </div>
        )}
        {activeTab === 'transactions' && (
          <div className="tab-content">
            <OhipTransactionsTab entityType="partner" entityId={partnerId} />
          </div>
        )}
        {activeTab === 'notifications' && (
          <div className="tab-content">
            <ProductNotificationsTab level="partner" id={partnerId} />
          </div>
        )}

        {activeTab === 'support' && (
          <div className="tab-content">
            <PartnerSupportTickets partnerId={partnerId} isSuperAdmin={isSuperAdmin} />
          </div>
        )}
      </div>
    </div>
  );
};

// TEMPLATES — Sync (the partner's sync templates) and Billing (its billing documents, V663). The sub-tab is in the URL
// (?sub=) like the tab, so a link opens on it; the shared SubTabs bar replaced the hand-styled one (STYLE.md).
const TEMPLATE_SUBTABS = [{ key: 'sync', label: 'Sync' }, { key: 'billing', label: 'Billing' }];

const TemplatesTab = ({ partnerId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('sub');
  const subTab = TEMPLATE_SUBTABS.some(t => t.key === requested) ? requested : 'sync';
  const changeSubTab = (next) => setSearchParams((prev) => {
    const params = new URLSearchParams(prev);
    params.set('tab', 'templates');
    params.set('sub', next);
    return params;
  }, { replace: true });

  return (
    <div>
      <SubTabs tabs={TEMPLATE_SUBTABS} active={subTab} onChange={changeSubTab} />
      {subTab === 'sync' && <PartnerSyncTemplates partnerId={partnerId} />}
      {subTab === 'billing' && <BillingTemplatesList partnerId={partnerId} />}
    </div>
  );
};

export default PartnerSettings;
